import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IRowData } from "@multiweb/types/contracts";
import { CaseInsensitiveMap } from "@multiweb/types/multiweb";
import { Code, Key } from "Global/Base/Utils/EventUtils";
import { ObjectUtils } from "Global/Base/Utils/ObjectUtils";
import { StringUtils } from "Global/Base/Utils/StringUtils";
import { Enum } from "OW.Remoting/Remoting/DataTypes/Enums";
import { ExtendedPropertiesType } from "UI/types";
import { SelectionHelper } from "UI/UI.Controls/DataGridSubcontrols/Components/SelectionHelper";
import { getScrollBarWidth } from "UI/UI.ElementarControls/Utils/CustomElementUtils";
import {
    AdditionalOrdering,
    IDataSource,
    IDataSourceLoadInfo,
    IOrderBy,
    OrderType,
} from "../../Global/Base/DataSources/Interfaces";
import { IDisposable } from "../../Global/Base/IDisposable";
import { CancellationToken, CancellationTokenSource } from "../../Global/Base/Promises/CancellationTokenSource";
import { PromiseUtils } from "../../Global/Base/Promises/PromiseUtils";
import { AttrValueReader } from "../../OW.Remoting/Remoting/AttrValueReader";
import { IAwaitableEvent } from "../UI.BaseControls/AwaitableEvent";
import { Control } from "../UI.BaseControls/Control";
import { IBooleanDetailValueEvent, IBooleanDetailValueEventData } from "../UI.BaseControls/IEvent";
import { ElementUtils } from "../UI.ElementarControls/Helpers/ElementUtils";
import { UIManager } from "../UI/UI/UIManager";
import { DataGridSharedMethods } from "./DataGridSharedMethods";
import { Column } from "./DataGridSubcontrols/Columns/Column";
import { DataRow, IDoubleClickEventArgs } from "./DataGridSubcontrols/Components/DataRow";
import { HeaderRow } from "./DataGridSubcontrols/Components/HeaderRow";
import { IDataRowElement, IEditableDataRowElement, RowClickEvent } from "./DataGridSubcontrols/Components/IDataRow";
import { EmptyDataGridMessage } from "./DataGridSubcontrols/DataGridMessages/EmptyDatagridMessage";
import {
    EmptyDatagridMessageType,
    IEmptyDataGridMessage,
} from "./DataGridSubcontrols/DataGridMessages/IEmptyDataGridMessage";
import { IDataGrid, IEmptyMessageDataGrid } from "./IDataGrid";
import {
    GlobalCustomEvent,
    ICellValueChangedPrewAwaitableEvent,
    IColumnOrderingChangedEvent,
    IColumnOrderingChangedEventData,
    IColumnPositionChangedEvent,
    IDataGridDataShownEvent,
    IDataGridDataShownEventData,
    IDataGridSelectionChangedEvent,
    IDataGridSelectionChangedEventData,
} from "./IEvent";

// eslint-disable-next-line unused-imports/no-unused-vars
export interface DataGrid<R extends IDataRowElement> {
    registerEventListener(type: "SelectionChanged", listener: (ev: IDataGridSelectionChangedEvent) => void, customId?: string): void;
    registerEventListener(type: "SelectionChangedPreview", listener: (ev: IAwaitableEvent<boolean>) => void, customId?: string): void;
    registerEventListener(type: "SelectionAll", listener: (ev: CustomEvent) => void, customId?: string): void;
    registerEventListener(type: "CellValueChangedPreview", listener: (ev: ICellValueChangedPrewAwaitableEvent) => void, customId?: string): void;
    registerEventListener(type: "Attached", listener: (ev: CustomEvent) => void, customId?: string): void;
    registerEventListener(type: "DataShown", listener: (ev: IDataGridDataShownEvent) => void, customId?: string): void;
    registerEventListener(type: "ReadOnlyChange", listener: (ev: IBooleanDetailValueEvent) => void, customId?: string): void;
    registerEventListener(type: "BeforeOrder", listener: (ev: CustomEvent) => void, customId?: string): void;
    registerEventListener(type: "OrderingChanged", listener: (ev: IColumnOrderingChangedEvent) => void, customId?: string): void;
    registerEventListener(type: "ColumnPositionChanged", listener: (ev: IColumnPositionChangedEvent) => void, customId?: string): void;
    registerEventListener(type: string, listener: EventListener, customId?: string): void;
}
const logger = LazyLoadingSectionLogger.create(LoggingSection.dataGrid);

export const ROW_SELECTION_KEY_CODES: string[] = [Code.ArrowUp, Code.ArrowDown, Code.PageUp, Code.PageDown, Code.Home, Code.End];

export abstract class DataGrid<R extends IDataRowElement> extends Control implements IEmptyMessageDataGrid<R> {
    //#region Events
    //#endregion Events

    //#region Listeners
    protected columnWidthChangeListener: (sender: any, prm: any) => void = () => {};
    //#endregion Listeners

    //#region Properties
    //#endregion Properties

    //#region Fields
    private _extendedProperties: CaseInsensitiveMap<ExtendedPropertiesType>;
    protected _dataSource: IDataSource;
    protected _dataLoadingPaused: boolean = false;

    protected _interactive: boolean;

    protected _scrollToSelectingRow = false;

    public _orderBy: IOrderBy[];
    public get orderBy(): IOrderBy[] {
        return this._orderBy;
    }
    public set orderBy(value: IOrderBy[]) {
        this._orderBy = value;
        if (this.columns)
            this.headerRow.renderOrderBy(this);
    }

    public set interactive(v: boolean) {
        this._interactive = v;

        if (this.columns != null) {
            for (const c of this.columns) {
                c.interactive = v;
            }
        }

        this.classList.toggle("ReadOnly", !v);
        this.callEvent("interactiveChanged", { value: v } as IBooleanDetailValueEventData);
    }

    public get interactive(): boolean {
        return this._interactive;
    }

    public multiSelect: boolean;

    public renderControlElement: boolean;

    protected _dataLoaded: boolean = false;

    /**Html reprezentace datove tabulky*/
    protected _dataTable: HTMLElement;
    protected _dataTableOffset: number;

    protected _resizeDiv: HTMLDivElement;

    /**Seznam sloupcu datagridu*/
    protected _columns: Array<Column>;

    private _color: string;

    public objectName: string;

    public fetchCount: number;
    public flagAttributeName: string;
    /**
     * Pole barev flagů, které je možno použít pro označení řádku
     */
    public allowedFlagColors: string[];

    public allowSorting: boolean;
    /**
     * Gets or sets the Cascading Style Sheet (CSS) class rendered by the Web server
     * control on the client.(Inherited from <a href="https://msdn.microsoft.com/en-
     * us/library/system.web.webcontrols.webcontrol%28v=vs.110%29.aspx"><font
     * color="#0000ff"><u>WebControl</u></font></a>.)
     */
    public cssClass: string;
    public ctrlID: string;

    /**
     * index posledni doctene stranky
     */
    public currentPageIndex: number;

    /**
     * Zakazuje nebo povoluje zmenu zobrazeni mezi dvouradkem a DataGridem napr. LOV
     * naseptavac
     */
    public disableViewChanges: boolean;
    /**
     * Zpusob zobrazeni (kompaktni / DataGrid)
     */
    public displayMode: string;

    /**
     * obsahuje Timer, ktery je vyhodnocovan pri zmene objektu pomoci sipek a aktualni
     * objekt se nacte az po nem, aby nedochazelo k prodleve pri rychlem pohybu sipkami
     */
    public getSelectedRowInfoTimer: number;
    protected _isSelectedAll: boolean;
    public initialized: boolean;
    public keyPressRowIndex: number; // index radku pri pohybu sipkami up/down + shift
    protected _lastScrollEvent: string = null;
    protected _selectionStartRow: R; // starting row for shift selection
    protected _lastClickedRow: R;
    private _dataLoadCounter: number = 0; //Počítadlo aktuálně běžících loaderů pro vykreslení dat
    private _currentSkippedRows: number = 0;

    private _skippedRowsCount: number = 0;
    public get skippedRowsCount(): number {
        return this._skippedRowsCount;
    }
    public set skippedRowsCount(value: number) {
        this._skippedRowsCount = value;
    }

    public get startIndex(): number {
        return this.dataRows.length + this._skippedRowsCount;
    }

    protected _selectionHelper: SelectionHelper;

    public get selectionStartRow(): R {
        return this._selectionStartRow;
    }

    public set selectionStartRow(value: R) {
        this._selectionStartRow = value;
    }

    public get lastClickedRow(): R {
        return this._lastClickedRow;
    }

    public set lastClickedRow(value: R) {
        this._lastClickedRow = value;
        this.keyPressRowIndex = this.dataRows.indexOf(value);
    }

    public set isSelectedAll(v: boolean) {
        this._isSelectedAll = v;
        this._selectionHelper.isSelectAll = v;
    }

    public get isSelectedAll(): boolean {
        return this._isSelectedAll;
    }

    public clearIsSelectedAllIfNeeded(): void {
        // Pokud je vybrán jen jeden záznam, pak musí být režim "vybráno vše" vypnutý.
        // Bez ohledu na cokoliv dalšího.
        if (this.selectedRows?.length <= 1) {
            this._isSelectedAll = false;
        }
    }

    public override get scrollLeft(): number {
        return this._listDiv.scrollLeft;
    }
    public override set scrollLeft(value: number) {
        this._listDiv.scrollLeft = value;
    }

    public override get scrollTop(): number {
        return this._listDiv.scrollTop;
    }

    public override set scrollTop(value: number) {
        this._listDiv.scrollTop = value;
    }

    public override get scrollHeight(): number {
        return this._listDiv.scrollHeight;
    }

    public override get scrollWidth(): number {
        return this._listDiv.scrollWidth;
    }

    public set columns(v: Array<Column>) {
        const oldv = this._columns;
        this._columns = v;
        v.forEach(column => {
            if (!column.columnWidthChange.containsListener(this.columnWidthChangeListener)) {
                column.columnWidthChange.add(this.columnWidthChangeListener);
            }
        });
        //this.Render();
        if (!oldv)
            this._attachDataGrid();
    }

    public get columns(): Array<Column> {
        return this._columns;
    }

    public get dataSource(): IDataSource {
        return this._dataSource;
    }

    public set dataSource(dataSource: IDataSource) {
        this._dataSource = dataSource;
        this._selectionHelper = new SelectionHelper(dataSource.condition, dataSource.dataKey);
    }

    /**
     * Vrátí true, pokud je element skutečně viditelný.
     */
    public get isElementVisible(): boolean {
        const boundingBox = this.getBoundingClientRect();
        const isVisible = getComputedStyle(this).visibility === "visible";
        return boundingBox.width && boundingBox.height && isVisible;
    }

    //proměnná pro zobrazení loaderu, než se datagrid přejde načte první data - aby se zobrazil loader, přes empty stav datagridu a bylo jasné, že se data ještě budou načítat.
    //undefined - datagrid nemá datasource, nebo ještě nebyl inicializován. False - datagrid byl incializovaán a zobrazí se loader pro první načtení dat. True - Datagrid načetl první data, skryje loader a dále už se nebude používat.
    private __firstDataLoaded: boolean | undefined = undefined;

    private _setFirstDataLoaded(val: boolean): void {
        //pokud grid není aktivní, nebude loader pro první data vůbec používat.
        if (this.__firstDataLoaded == undefined && (!this.visible || !this.enabled)) {
            this.__firstDataLoaded = true;
            return;
        }

        //datagrid ještě neobdržel první data (nebo informaci, že žádná nemá), zobrazí loader
        if (this.__firstDataLoaded == undefined && !val) {
            UIManager.instance.showLoader();
            this.__firstDataLoaded = false;
        } else if (this.__firstDataLoaded == false && val) { //Data byla obdržena, skryje loader
            UIManager.instance.hideLoader();
            this.__firstDataLoaded = true;
        }
    }

    private _afterFetchNoReselectCallback: () => void;

    /**
     * Pocet radku doctenych v jednom dotazu
     */
    public pageSize: number;
    public renderHeader: boolean;
    protected _refreshSelection: boolean;
    public selectedRows: Array<R>;
    /**
     * Identifikátory naposledy vybraných řádků.
     * Kvůli dědícím třídám astavovat POUZE přes metody _setLastSelectedDataKeys,
     * _concatenateLastSelectedDataKeys, _removeFromLastSelectedDataKeys,
     * _clearLastSelectedDataKeys!
     */
    protected _lastSelectedDataKeys: Array<string> = [];
    private _autoSelectFirstRecord: boolean;
    private _dontKeepChangedRecords: boolean;

    public get autoSelectFirstRecord(): boolean {
        return this._autoSelectFirstRecord;
    }

    public set autoSelectFirstRecord(value: boolean) {
        const old_value = this._autoSelectFirstRecord;
        this._autoSelectFirstRecord = value;
        // dochazelo pokud byl seznam i detail k volani objektu 2x
        if (old_value !== value && value)
            this.selectFirstRow(value);
    }

    /**
     * Příznak, že se při aktualizaci dat nemají zachovávat záznamy, které jsou označené nebo změněné, pokud nejsou v nové dávce dat.
     */
    public get dontKeepChangedRecords(): boolean {
        return this._dontKeepChangedRecords;
    }

    public set dontKeepChangedRecords(value: boolean) {
        this._dontKeepChangedRecords = value;
    }

    private _onlyIfOneRow: boolean;

    public set autoSelectOnlyIfOneRow(value: boolean) {
        this._onlyIfOneRow = value;
    }

    public headerRow: HeaderRow;
    /**
     * Příznak, zda se má vykreslovat patička.
     */
    private _renderFooterEnabled: boolean;
    /**
     * Element reprezentující obsah patičky.
     */
    private _footerContent?: HTMLElement;
    /**
     * Element reprezentující patičku.
     */
    private _footerElement: HTMLElement;
    /**
     * Element který se roztahuje do dostupného místa mezi obsahem a patičkou, aby byla patička na spodní
     * straně i v případě, že obsah gridu je menší než dostupná plocha.
     */
    private _fillerElement?: HTMLElement;

    public showHeader: boolean;
    /**
     * ceka az DataReader vrati data (pokud True, tak se neodesle zadost na data)
     */
    public waitingForData: boolean;

    //public OwnerFrame: MasterableFrame;

    /**
     * Kolekce radku zobrazenych v seznamu
     */
    public dataRows: R[];

    protected _mainDiv: HTMLElement;
    protected _mainListDiv: HTMLElement;
    protected _listDiv: HTMLElement;
    public emptyMessage: IEmptyDataGridMessage;
    protected _bindingData: boolean;

    protected _rowControlsFactory?: (dataKeyFetch: () => string) => HTMLElement;
    protected _dataRowFactory: (dataGrid: IDataGrid<R>, data: IRowData) => R;
    private _isDataGridDisposed = false;
    private static __visibilityObserver: IntersectionObserver;
    private static __resizeObserver: ResizeObserver;

    private get _visibilityObserver(): IntersectionObserver {
        if (!DataGrid.__visibilityObserver) {
            DataGrid.__visibilityObserver = new IntersectionObserver((entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        const dataGrid = (entry.target as DataGrid<R>);
                        dataGrid._recalculateAfterVisible();

                        // slouží to čistě ke kontrole zobrazení a případnému zavolání dočítání dat,
                        // takze pokud sou načtený všechny data nebo je viditelný scrollbar ignorujeme to
                        if (dataGrid.dataRows.length === 0 || dataGrid._allDataRead || ElementUtils.hasScrollBar(dataGrid._listDiv)) {
                            continue;
                        }
                        dataGrid.resizeGrid();
                    }
                }
            }, { rootMargin: "0px", threshold: 0.01 });
        }

        return DataGrid.__visibilityObserver;
    }

    private get _resizeObserver(): ResizeObserver {
        if (!DataGrid.__resizeObserver) {
            DataGrid.__resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const dataGrid = (entry.target as DataGrid<R>);
                    if (dataGrid._allDataRead || dataGrid.clientWidth === 0 || dataGrid.clientHeight === 0) {
                        continue;
                    }
                    dataGrid.resizeGrid();
                }
            });
        }
        return DataGrid.__resizeObserver;
    }

    private _lastSelectedIndex: number = -1;

    /** Index naposledy smazaného záznamu */
    private _lastDeletedRowIndex: number = 0;
    //#endregion Fields

    //#region HtmlElement
    protected override _constructor(): void {
        super._constructor();
        this._interactive = true;
        this.multiSelect = true;
        this.renderHeader = true;
        this.renderControlElement = true;

        this._refreshSelection = true;
        this._dataTableOffset = 0;

        this.fetchCount = 5;
        this.classList.add("main-content");

        this._mainListDiv = document.createElement("div");
        this._mainListDiv.classList.add("main-list-div");

        this.appendChild(this._mainListDiv);

        this.dataRows = new Array<R>();
        this.selectedRows = new Array<R>();
        this.lastClickedRow = null;
        this.autoSelectFirstRecord = false;
        this._onlyIfOneRow = true;

        this.allowSorting = true;
        this.currentPageIndex = 0;

        this.emptyMessage = new EmptyDataGridMessage();

        this.setAttribute("tabindex", "0");
        this._eventsController.addListener(this, "keydown", (e: KeyboardEvent) => {
            DataGrid.keyDown(e, this);
        });
        this._eventsController.addListener(this, "keypress", (e: KeyboardEvent) => {
            DataGrid.keyPress(e, this);
        });
        this._eventsController.addListener(this, "keyup", (e: KeyboardEvent) => {
            DataGrid.keyUp(e, this);
        });

        //this.registerEventListener("resize", (e: KeyboardEvent) => { logger.log("resize"); });
        this._eventsController.addListener(this, "canvalueload", () => { // Je voláno jen jednou při prvním zviditelnění gridu
            this._updateResizeDivSize();
            if (!this._allDataRead && this.dataRows.length > 0) {
                this.dataSource.getNextData(this.startIndex, this.fetchCount);
            }
            this._updateEmptyMessageVisibility(); // Řeší první přepnutí DataGrid/ComapctDataGrid při změně velikosti stránky
        });

        this._visibilityObserver.observe(this);
        this._resizeObserver.observe(this);

        this._dataRowFactory = () => new DataRow() as any as R;
    }

    /**
     * Je zavoláno vždy po zobrazení DG pokud byl skrytý ať už styly nebo právama
     * @protected
     */
    protected abstract _recalculateAfterVisible(): void;

    /**
     * Kontrola zda se muze zmenit vyber radku. Akce muze byt zablokovana pokud je jiny seznam rozeditovany a neulozeny.
     */
    protected async _checkIfCanSelectionChange(evt: MouseEvent, row: R): Promise<boolean> {
        if (row.isSelected && !this._canChangeSelectedRowMultiSelect(evt))
            return true;

        return await DataGridSharedMethods.canChangeSelectedRow(row);
    }

    /**
     * Subkontrola zda se muze zmenit vyber radku.
     */
    protected _canChangeSelectedRowMultiSelect(evt: MouseEvent): boolean {
        return (this.multiSelect && !evt?.ctrlKey && !evt?.shiftKey && (this.selectedRows.length > 1));
    }

    protected _getRowIndex(row: R): number {
        return this.dataRows.findIndex(dataRow => dataRow.dataKey === row.dataKey);
    }

    protected _rowClickSelectItem(evt: MouseEvent, row: R): boolean {
        // click + Ctrl
        if (evt.ctrlKey || evt.metaKey) {
            this.focus();

            if (!row.isSelected) {
                row.isSelected = true;
                this._selectionHelper?.include([row.dataKey]);
            } else {
                row.isSelected = false;
                this._selectionHelper?.exclude([row.dataKey]);
            }

            this.lastClickedRow = row;
            this.selectionStartRow = row;
        } else {
            // click + Shift
            if (evt.shiftKey) {
                this.focus();

                this.isSelectedAll = false;
                // mam uz oznaceny radek (radky)
                if (this.selectedRows.length > 0) {
                    let fromIndex = this._getRowIndex(this.selectionStartRow);
                    let toIndex = this._getRowIndex(row);
                    this.keyPressRowIndex = toIndex;
                    this.selectionStartRow = this.dataRows[fromIndex];
                    if (fromIndex > toIndex) { //v pripade ze index prvniho je vetsi, tak prohodime
                        [fromIndex, toIndex] = [toIndex, fromIndex];
                    }
                    this.clearSelectedRows(); // vymazeme oznacene radky
                    const selectedIds = [];
                    for (let i = fromIndex; i <= toIndex; i++) { // prochazime a oznacime radky od fromIndexu po toIndex
                        this.dataRows[i].isSelected = true;
                        selectedIds.push(this.dataRows[i].dataKey);
                    }
                    this._selectionHelper?.set(selectedIds);
                    this.lastClickedRow = row;
                    this.keyPressRowIndex = toIndex; // pri oznaceni dalsich zaznamu pres Shift+Up/Down chceme pokracovat na pozici toIndex
                } else { // jeste nebyl oznacen zadny radek - chova se jako obycejny click
                    if (!row.isSelected) {
                        this.clearSelectedRows();
                        row.isSelected = true;
                    }
                    this.selectionStartRow = row;
                    this.lastClickedRow = row;
                }
            } else {
                // just click
                return this._rowSimpleClick(row);
            }
        }

        return true;
    }

    protected async _rowClick(e: GlobalCustomEvent<RowClickEvent>, row: R): Promise<void> {
        const evt = e.detail;

        if (!this._selectionHelper) logger.debug("SelectionHelper is not defined");

        if (!this.interactive)
            return;

        if (!(await this._checkIfCanSelectionChange(evt.originalEvent, row)))
            return;

        let dispEvent = true;

        let dblClick = false;
        if (evt.clickCount == 0)
            return;
        if (evt.clickCount == 2)
            dblClick = true;

        if (this.multiSelect) {
            dispEvent = this._rowClickSelectItem(evt.originalEvent, row);
        } else {
            this._rowSimpleClick(row);
        }

        let rowID: string = null;
        if (this.selectedRows.length == 1) {
            rowID = this.selectedRows[0].dataKey;
        }

        if (dispEvent) {
            // pokud je vybrany jen jeden radek
            if (rowID != null) {
                this._dispatchSelectionChangeEvent(rowID, { doubleClick: dblClick, clickedRow: this });
            } else { // neni vybrany zadny radek nebo oznaceno vice zaznamu
                this._dispatchSelectionChangeEvent("-1", { doubleClick: false, clickedRow: this });
            }
        }
        if (dblClick)
            this._dispatchDoubleClickEvent(dispEvent, rowID);
    }

    protected _rowSimpleClick(row: R): boolean {
        if (!row.isSelected || (row.isSelected && (this.selectedRows.length > 1))) {
            this.focus();
            this.isSelectedAll = false;
            this.clearSelectedRows();
            row.isSelected = true;
            this.lastClickedRow = row;
            this.selectionStartRow = row;
            this._selectionHelper?.set([row.dataKey]);
            return true;
        } else {
            return false;
        }
    }

    // TODO: Upravit
    protected _dispatchDoubleClickEvent(selectionByDoubleClick: boolean, rowID: string = undefined): void {
        this.callEvent<IDoubleClickEventArgs>("DoubleClick", { selectionByDoubleClick, rowID }, true, true);
    }

    public setFooterContent(content: HTMLElement): void {
        this._footerContent = content;
        this._renderFooter();
    }

    public setRenderFooter(renderFooter: boolean): void {
        this._renderFooterEnabled = renderFooter;
        this._renderFooter();
    }

    /**
     * Zaregistruje tovární metodu, pomocí které se budou vytvářet elementy řádků
     * @param factory Tovární metoda vytvářející elementy řádku
     */
    public registerDataRowFactory(factory: (dataGrid: IDataGrid<R>, data: IRowData) => R): void {
        this._dataRowFactory = factory;
    }

    /**
     * Umožňuje zaregistrovat vlastní tovární funkce, která se používá při vytváření ovládacích prvků pro každý řádek
     * @param factory Tovární funkce
     */
    public registerRowControlsFactory(factory: (dataKeyFetch: () => string) => HTMLElement): void {
        this._rowControlsFactory = factory;
    }

    /**
     * Vyvola novou udalost, ktera se pouziva pri zmene vybranych radku
     * @param rowID -
     * @param data -
     */
    protected _dispatchSelectionChangeEvent(rowID: string, data: any): void {
        this.callEvent("SelectionChanged", { dataGrid: this, rowID: rowID, data: data } as IDataGridSelectionChangedEventData, true, true);
    }
    protected _dispatchKeyboardSelectionChangeInitiated(): void {
        this.callEvent("KeyboardSelectionChangeInitiated", null, true, true);
    }

    // You can also define the other lifecycle methods.
    protected override connectedCallback(): void {
        super.connectedCallback();
    }

    protected override disconnectedCallback(): void {
        super.disconnectedCallback();
        this.callEvent("detached");
        this._eventsController.removeByKey("dataGridWindowReisze");
    }

    //#endregion HtmlElement

    //#region Methods

    private _resizeCTS: CancellationTokenSource;
    private _dataLoadingEndedCTS: CancellationTokenSource;

    /**
     * Vola se pokud je resizovan formular nebo cokoliv jineho na co potrebuje grid zareagovat a docist zaznamy kvuli ztrate scrollbaru
     */
    public resizeGrid(eventData?: IDataSourceLoadInfo): void {
        if (!this._listDiv || !this.dataSource || !this.initialized || this.emptyMessage.messageType === EmptyDatagridMessageType.queryModeEmpty || !this.visible || !this.canValueLoad) {
            return;
        }

        // Don't need to load more if all data is read or element is invisible or has scrollbar and empty message is not initial empty.
        const doNotLoadData = () =>
            this._allDataRead || !this.isElementVisible ||
            (ElementUtils.hasScrollBar(this._listDiv) && this.emptyMessage.messageType !== EmptyDatagridMessageType.initialEmpty);

        if (doNotLoadData()) {
            const { promise, cts } = PromiseUtils.cancelPrevAndDelayCall(this._dataLoadingEndedCTS, 300, () => {
                this.callEvent("DataLoadingEnded");
            });
            promise.catch(PromiseUtils.cancellationCatch);
            this._dataLoadingEndedCTS = cts;
            return;
        }

        const formGroupContainer = this.closest(".form-group-container");
        if (formGroupContainer) {
            const classes = [...formGroupContainer.classList];
            if (classes.some(element => element === "form-group-container--hidden" || element === "inactive")) {
                this._dataLoadingPaused = true;
                return;
            } else {
                this._dataLoadingPaused = false;
            }
        }

        const { promise, cts } = PromiseUtils.cancelPrevAndDelayCall(this._resizeCTS, 300, (token: CancellationToken) => {
            // Check again if the result of the condition has not changed.
            if (doNotLoadData()) {
                this.callEvent("DataLoadingEnded");
                return;
            }
            this.dataSource.getNextData(this.startIndex, this.fetchCount, (eventData && eventData.keepSelectedRecords), token);
        });

        promise.catch(PromiseUtils.cancellationCatch);
        this._resizeCTS = cts;
    }

    protected _attachDataGrid(): void {
        this._attachEmptyMessage();
        this.initialize();
        this.preRender();
        this.render();

        this.initialized = true;
        this.callEvent("Initialized");
    }

    /** Inicializuje box se zprávou o stavu DataGridu */
    protected _attachEmptyMessage(): void {
        this._mainListDiv.appendChild(this.emptyMessage);

        this._updateEmptyMessageVisibility(); // Jinak se nezobrazí pro seznamy, které ani nezačnou načítat data - např. protože čekají na výběr mastera

        this.emptyMessage.registerEventListener("visibility", (e: IBooleanDetailValueEvent) => {
            if (this._resizeDiv)
                this._resizeDiv.style.display = e.detail.value ? "block" : "none";
        });
    }

    /**
     * Inicializuje datagrid a preda nejaky info datasourcu
     */
    public initialize(): void {
        if (!this.dataSource) {
            return;
        }

        this._eventsController.add(this.dataSource.dataLoaded, async (_sender, eventData) => {
            this._setFirstDataLoaded(true);
            if (!this.visible || (eventData.cancellationToken && eventData.cancellationToken.cancellationRequested))
                return;

            if (eventData.refreshedData)
                this._refreshRowsWithData(eventData);
            else
                this._showData(eventData);

            if (!this.classList.contains("data-loaded"))
                this.classList.add("data-loaded");

            this._updateEmptyMessageVisibility(eventData.isFiltered, eventData.fulltextSearchString);
        });

        this._eventsController.add(this.dataSource.loadingStarted, () => {
            this._setFirstDataLoaded(false);
            this._dataLoadCounter++;
            this._updateEmptyMessageVisibility();
        });

        this._eventsController.add(this.dataSource.loadingCancelled, (_sender, eventData) => {
            this._setFirstDataLoaded(true);
            this._updateEmptyMessageVisibility(eventData.isFiltered, eventData.fulltextSearchString);
        });

        this._eventsController.add(this.dataSource.loadingEnded, async (_sender, eventData) => {
            this._setFirstDataLoaded(true);
            this._dataLoadCounter--;
            this._updateEmptyMessageVisibility(eventData.isFiltered, eventData.fulltextSearchString);
        });

        for (const column of this._columns) {
            this._eventsController.addListener(column.headerTemplate, "focus", (e) => {
                if (e.target instanceof HTMLElement) {
                    const offset = 50; // posun, aby to nebylo az moc u kraje
                    const focusElStart = e.target.offsetLeft;
                    const focusElEnd = focusElStart + e.target.offsetWidth;
                    const gridStart = this.scrollLeft + this.headerRow.getChoiceElementWidth();
                    const gridEnd = this.scrollLeft + this.offsetWidth - offset;

                    if (focusElEnd > gridEnd) {
                        this.scrollLeft += e.target.offsetWidth;
                    }
                    if (focusElStart < gridStart) {
                        this.scrollLeft = focusElStart - this.headerRow.getChoiceElementWidth();
                    }
                }
            });
        }
    }

    protected _refreshRowsWithData(eventData: IDataSourceLoadInfo): void {
        // odsazení posledního označeného řádku od vrchního okraje datagridu - kvůli zachování pozice
        const lastClickedRowOffset = this.lastClickedRow ? ElementUtils.offset(this.lastClickedRow).top - ElementUtils.offset(this._listDiv).top : null;

        // kopie, ze které se později mažou zpracované řádky
        const tempRows: Map<string, IDataRowElement> = new Map();

        this.dataRows.filter(i => !ObjectUtils.isIdTemporary(i.dataKey)).forEach((r) => tempRows.set(r.dataKey.toString(), r));

        const newRows: { newRowData: IRowData; insertAfter: Element }[] = [];

        let lastMatchedRow: IDataRowElement = null;

        this._bindingData = true;

        for (const rowData of eventData.dataGenerator() as Iterable<IRowData>) {
            const id = rowData.ID.toString();
            const matchedRow = tempRows.get(id);
            if (matchedRow) {
                lastMatchedRow = matchedRow;

                if (!(lastMatchedRow instanceof IEditableDataRowElement) || !lastMatchedRow.isChanged) { // řádek není změněný, u změněného se rozeditované hodnoty nesmí samy změnit
                    // existujícímu řádku se nastaví nová data a vykreslí se změny
                    this._updateRowValues(lastMatchedRow, rowData);
                }

                // jelikož k tomuto řádku byla nalezena data v novém datasetu, tak se odstraňuje, protože je zpracovaný
                tempRows.delete(id);
            } else {
                // nové řádky k přidání - přidá se za poslední zpracovaný existující řádek
                newRows.push({ newRowData: rowData, insertAfter: lastMatchedRow });
            }
        }
        // přidání nových řádků, které přišly v novém datasetu, ale zatím nejsou zobrazeny
        const newRowsArray = newRows;

        // pokud je seznam prazdny, vkladame normalne
        if (this.dataRows.length > 0) newRowsArray.reverse();
        for (const rowInfo of newRowsArray) { // pokud přijde více nových záznamů v jednom bloku (budou se připojovat za stejným existujícím řádkem), tak je potřeba je přidávat v obráceném pořadí, aby se správně zařadily
            rowInfo.insertAfter = rowInfo.insertAfter ? rowInfo.insertAfter : null;
            const newRow = this._addRowAfter(rowInfo.newRowData, rowInfo.insertAfter);
            if (eventData.selectLoaded)
                newRow.isSelected = true;
        }

        let promise: Promise<void> = Promise.resolve();

        if (!eventData.keepExisting) {
            // odstranění starých řádků, které už  nejsou v nových datech
            if (tempRows.size > 0) {
                //záznamy nejsou v prvních 50. Ověříme jestli existují a splňuje podmínku datagridu.
                const oldRows = Array.from(tempRows.values());
                promise = this.dataSource.loadSpecificRecords(oldRows.filter(i => !i.dataKey.toString().startsWith("iid")).map(i => i.dataKey), false)
                    .then(existingRows => {
                        for (const row of oldRows) {
                            const newRowIndex = existingRows.findIndex(i => AttrValueReader.readRow(i, "ID") == row.dataKey);
                            if (newRowIndex === -1) //záznam už neexistuje nebo nesplňuje podmínku
                                row.isDeleted = true;
                        }
                    })
                    .catch(() => {
                        oldRows.forEach(r => r.isDeleted = true);
                    });
            }
        }

        promise.finally(() => this._finishDataRefresh(eventData, lastClickedRowOffset));

        this.callEvent("DataShown", { data: eventData.dataGenerator } as IDataGridDataShownEventData);
    }

    private _finishDataRefresh(eventData: IDataSourceLoadInfo, lastClickedRowOffset: number): void {
        this._bindingData = false;

        this._updateEmptyMessageVisibility();

        if (eventData.selectLoaded) {
            return;
        } // pokud přišly řádky, které již byly označeny, nechám být, mohly by zalézat za viditelnou část DG

        // doscrolluje tak, aby byl poslední nakliknutý element stále na stejném místě
        if (lastClickedRowOffset === null)
            return; // byl nějaký řádek vybraný

        const lastClickedRowIndex = this.lastClickedRow ? Array.prototype.indexOf.call(this._dataTable.children, this.lastClickedRow) : -1;
        if (lastClickedRowIndex === -1)
            return;

        this._listDiv.scrollTop = (this.lastClickedRow.clientHeight + 1) * lastClickedRowIndex - lastClickedRowOffset;
        if (this._listDiv.scrollTop < 0)
            this._listDiv.scrollTop = 0;
    }

    protected _updateRowValues(row: IDataRowElement, rowData: IRowData): void {
        row.init(rowData, this, null);
        row.render();
    }

    protected _addRowAfter(newRowData: IRowData, existingRow: Element): IDataRowElement {
        const newRow = this._createRow(newRowData);
        if (existingRow) {
            existingRow.insertAdjacentElement("afterend", newRow.render());
        } else {
            this._dataTable.insertAdjacentElement("afterbegin", newRow.render());
        }

        return newRow;
    }

    protected _removeRow(dataRow: IDataRowElement): void {
        dataRow.remove();

        this.removeRow(dataRow.dataKey);
    }

    /**
     * Aktualizuje hlášku zobrazovanou při prázdném seznamu
     * @param isFiltered Zda je aktivní filtr
     */
    protected _updateEmptyMessageVisibility(isFiltered: boolean = false, fulltextSearchString?: string): void {
        if (this.checkAndLogDisposedUsage("_updateEmptyMessageVisibility")) return;

        this.emptyMessage.fulltextSearchString = fulltextSearchString;

        const notDeletedDataRows = this.dataRows.filter(row => !row.isDeleted);
        if (notDeletedDataRows.length === 0) {
            this.classList.add("is-empty");
            this.emptyMessage.visible = true;

            if (this._dataLoadCounter > 0) {
                this.emptyMessage.messageType = EmptyDatagridMessageType.dataLoading;
            } else if (isFiltered) {
                this.emptyMessage.messageType = EmptyDatagridMessageType.filterEmpty;
            } else if (!StringUtils.isNullOrEmpty(fulltextSearchString)) {
                this.emptyMessage.messageType = EmptyDatagridMessageType.fulltextEmpty;
            } else if (this.emptyMessage.messageType !== EmptyDatagridMessageType.queryModeEmpty) {
                this.emptyMessage.messageType = EmptyDatagridMessageType.initialEmpty;
            }

            return;
        }

        this.classList.remove("is-empty");
        this.emptyMessage.visible = false;
        this._recalculateAfterVisible();
    }

    /**
     * Nastavuje grid do režimu QueryMode, kdy se nezobrazují žádná data dokud uživatel neprovede akci
     * @param dataLoad Funkce, která se má spustit po kliknutí na tlačítko pro zobrazení dat
     */
    public setToQueryMode(dataLoad: Function): void {
        this.clearDataRows();

        this.emptyMessage.setTypeToQueryMode(dataLoad);

        this._updateEmptyMessageVisibility();
    }

    /** Ověřuje, zda je DataGrid v režimu QueryMode */
    public isInQueryMode(): boolean {
        return this.emptyMessage.messageType === EmptyDatagridMessageType.queryModeEmpty;
    }

    /**
     * Nastaví metodu, která se má provést, pokud se po načtení dat neprovádí znovuoznačení záznamu
     */
    public setAfterFetchNoReselectCallback(method: () => void): void {
        this._afterFetchNoReselectCallback = method;
    }

    protected _showData(eventData: IDataSourceLoadInfo): void {
        if (eventData.cancellationToken)
            eventData.cancellationToken.throwIfCancellationRequested();

        if (eventData.startIndex === 0) {
            const isSelectedAll = this.isSelectedAll;
            this._refreshSelection = true;
            this._setLastSelectedDataKeys(this.selectedRows);
            this.clearDataRows(eventData?.isFiltered, eventData?.fulltextSearchString);
            this.clearSelectedRows();
            this.isSelectedAll = isSelectedAll;
        }

        this._allDataRead = eventData.lastBatch;

        this._bindingData = true;

        this._createRows(eventData.dataGenerator());

        this._renderData(eventData);

        this._bindingData = false;

        // ve vyjimecnych pripadech dochazelo k tomu, ze se data docitala jeste pred vykreslenim DG, a proto se spatne pocitala vyska pro fetch count
        if (!this.isAttached) {
            this._eventsController.addListener(this, "attached", () => {
                this._fetchNextBatch(eventData);
            }, { options: { once: true } });
        } else {
            if (!this._scrollToSelectingRow)
                this._scrollToSelectingRow = eventData.startIndex == 0 && this._lastSelectedDataKeys.length > 0;
            this._fetchNextBatch(eventData);
        }

        if (eventData.startIndex === 0) {
            this._clearLastSelectedDataKeys();
        }
    }

    protected async _fetchNextBatch(eventData: IDataSourceLoadInfo): Promise<void> {
        //logger.log("Fetching data: startIndex: " + eventData.startIndex + " rowCount: " + this.dataRows.length);

        // Pokud má datagrid výšku nebo šířku 0, tak je seznam skrytý (např. ve split panelu) a není potřeba do něj dotahovat data.
        if (!this._allDataRead && !ElementUtils.hasScrollBar(this._listDiv) && this.isElementVisible) {
            this.dataSource.getNextData(this.startIndex, this.fetchCount, eventData.keepSelectedRecords);
        } else {
            this.callEvent("DataShown", { data: eventData.dataGenerator } as IDataGridDataShownEventData);
        }

        if (eventData.keepSelectedRecords !== false) { // je potřeba udělat keepSelectedRows i když je dočtena poslední dávka záznamů
            await this.keepSelectedRows();
        }

        if (this.autoSelectFirstRecord)
            this.selectFirstRow(this._onlyIfOneRow);

        // pokud se nacitaji data od indexu 0 a je vybran jeden zaznam, pusti se selection change - spusti znovu nacteni dat na podrizenych seznamech
        if (eventData.startIndex === 0) {
            if (this.selectedRows.length === 1) {
                if (eventData.keepSelectedRecords && this.lastClickedRow && this._listDiv.querySelector(".DataRow:not(.frozen-row)[datakey='" + this.lastClickedRow.dataKey + "']")) {
                    this._dispatchSelectionChangeEvent(this.lastClickedRow.dataKey, { doubleClick: false, clickedRow: this.lastClickedRow });
                } else {
                    if (this._afterFetchNoReselectCallback)
                        this._afterFetchNoReselectCallback();
                }
                this._afterFetchNoReselectCallback = null;
            } else if (!this.isSelectedAll) {
                this._dispatchSelectionChangeEvent("-1", { doubleClick: false, clickedRow: this.lastClickedRow });
            }
            this._dataLoaded = true;
        }
    }

    public get allDataRead(): boolean {
        return this._allDataRead;
    }

    /**
     * Příznak, že už byly z DataSourcu načtena veškerá data
     */
    protected _allDataRead: boolean = false;
    //#endregion Properties

    protected _createRows(data: Iterable<IRowData>): void {
        let i = 0;
        let skippedRows = 0;
        let createdRows = 0;
        const fetchCount = this._currentSkippedRows > 0 ? this._currentSkippedRows : this.fetchCount;
        const startLng = this.dataRows.length;
        // If it's the first batch, we need to check selection in the whole batch.
        let missingSelectedCount = startLng > 0 ? 0 : this._lastSelectedDataKeys.length;
        for (const row of data) {
            const dataKeyValue = row[this.dataSource.dataKey].toString();
            if (!(this.dataSource && dataKeyValue != null && this.dataRows.some(dataRow => dataRow.dataKey === dataKeyValue))) { // Pokud již řádek v DG je, nebude se vytvářet znovu
                this._createComplexRow(row);
                createdRows++;
            } else {
                skippedRows++;
            }

            if (missingSelectedCount > 0 && this._lastSelectedDataKeys.includes(dataKeyValue)) {
                missingSelectedCount--;
            }

            i++;

            if (createdRows >= fetchCount) {
                // je treba nastavit allDataRead na false, pri prvnim cteni nacteni novych dat se nepredava fetchCount pro davku tak jako v metode getNextBatch
                if (startLng === 0)
                    this._allDataRead = false;

                // Don't break if there is possibility that the first loaded batch contains more data that could be selected.
                if (missingSelectedCount <= 0) {
                    break;
                }
            }
        }

        this.skippedRowsCount += skippedRows;
        if (i >= fetchCount && createdRows < fetchCount && skippedRows > 0) {
            // pokud jsou k dispozici další data a některé řádky byly přeskočeny a nepřidány do datarows, getujeme počet řádků chybějících do fetchCount
            this._currentSkippedRows = fetchCount - createdRows;
            this._getNextData(this._currentSkippedRows);
        } else {
            this._currentSkippedRows = 0;
        }
    }

    protected _createComplexRow(rowData: IRowData): R {
        return this._createRow(rowData);
    }

    protected _setLastSelectedDataKeys(selectedRows: Array<R>): string[] {
        this._lastSelectedDataKeys = selectedRows.map(row => row.dataKey);
        return this._lastSelectedDataKeys;
    }

    protected _concatenateLastSelectedDataKeys(selectedRows: Array<R>): string[] {
        this._lastSelectedDataKeys = this._lastSelectedDataKeys.concat(selectedRows.map(row => row.dataKey));
        return this._lastSelectedDataKeys;
    }

    protected _removeFromLastSelectedDataKeys(dataKeyToDeselect: string): string[] {
        this._lastSelectedDataKeys = this._lastSelectedDataKeys.filter(dataKey => dataKey !== dataKeyToDeselect);
        return this._lastSelectedDataKeys;
    }

    protected _clearLastSelectedDataKeys(): void {
        this._lastSelectedDataKeys = [];
    }

    public async refreshData(): Promise<void> {
        if (this._dataLoaded)
            await this.dataSource.refreshData(this.dataRows.length);
        else
            await this.dataSource.loadNewData();
    }

    /**
     * Smazani vsech radku
     */
    public clearDataRows(isFiltered: boolean = false, fulltextSearchString?: string): void {
        while (this.dataRows && this.dataRows.length !== 0) {
            const dataRow = this.dataRows.pop();
            dataRow.remove();
            dataRow.dispose();
        }
        this.skippedRowsCount = 0;
        this._updateEmptyMessageVisibility(isFiltered, fulltextSearchString);
    }

    /**
     * Smazani oznacenych radku
     * @param triggerSelectionChanged - zda má být vyvolána událost SelectionChanged
     */
    public clearSelectedRows(triggerSelectionChanged: boolean = false): void {
        for (const row of [...this.selectedRows]) {
            if (row.isDisposed()) { // TODO Provizorní kontrola. Opravit příčinu práce s disposed řádky v rámci tasku 33553.
                continue;
            }
            row.isSelected = false;
        }

        this.isSelectedAll = false;
        this.selectedRows = [];
        this.lastClickedRow = undefined;
        this.selectionStartRow = undefined;
        this._lastSelectedIndex = -1;

        if (triggerSelectionChanged) { // Se používá na MSLOV
            this.triggerSelectionChangedAfterTimeout("-1", false);
        }
    }

    /**
     * Udrzeni vybranych radku pri refresh/razeni seznamu
     */
    public async keepSelectedRows(): Promise<void> {
        let IDs = new Array<any>();

        if (!this._selectionHelper.masterSelectAll) {
            if (this._lastSelectedDataKeys.length > 0) {
                IDs = [...this._lastSelectedDataKeys];
            } else {
                for (const row of this.selectedRows) {
                    let datakey = row.dataKey;
                    if (datakey)
                        datakey = datakey.toString();
                    IDs.push(datakey);
                }
            }

            this.clearSelectedRows();

            if (IDs.length > 0) {
                const foundIDs: string[] = [];

                for (const row of this.dataRows) {
                    const rowDataKey = row.dataKey.toString();
                    const index = IDs.indexOf(rowDataKey);
                    if (index >= 0) {
                        row.isSelected = true;
                        foundIDs.push(rowDataKey);
                    }
                    if (this._selectionHelper.contains(rowDataKey))
                        row.isSelected = true;
                }

                const notFoundIDs = IDs.filter(id => foundIDs.indexOf(id) === -1);

                if (notFoundIDs.length > 0) {
                    await this.dataSource.loadSpecificRecords(notFoundIDs, true);
                }
            }
        } else {
            for (const row of this.dataRows) {
                const rowDataKey = row.dataKey.toString();
                row.isSelected = this._selectionHelper.contains(rowDataKey);
            }
        }

        if (this.selectedRows.length > 0) {
            this.lastClickedRow = this.selectedRows[this.selectedRows.length - 1];
            this.selectionStartRow = this.lastClickedRow;
            if (this._refreshSelection) {
                if (this.selectedRows.length === 1) {
                    this._dispatchSelectionChangeEvent(this.lastClickedRow.dataKey, { doubleClick: false, clickedRow: this.lastClickedRow, isRefreshSelection: true });
                } else {
                    this._dispatchSelectionChangeEvent("-1", { doubleClick: false, clickedRow: this.lastClickedRow, isRefreshSelection: true });
                }
                this._refreshSelection = false;
            }
        } else {
            if (IDs.length > 0)
                this._dispatchSelectionChangeEvent("-1", { doubleClick: false, clickedRow: this.lastClickedRow, isRefreshSelection: true });
        }
    }

    public async selectRow(dataKey: string): Promise<R> {
        const dataRow = this.dataRows.find((row) => row.dataKey === dataKey);
        if (dataRow) {
            dataRow.isSelected = true;
            this.lastClickedRow = dataRow;
            this.selectionStartRow = dataRow;
            this._dispatchSelectionChangeEvent(this.lastClickedRow.dataKey, { doubleClick: false, clickedRow: this.lastClickedRow });
        }
        return dataRow;
    }

    /**
     * Označí záznam na pozici naposledy smazaného záznamu.
     */
    public async selectRowOnLastDeletedPosition(): Promise<R> {
        let rowIndex = this._lastDeletedRowIndex;
        if (rowIndex >= this.dataRows.length) {
            rowIndex = this.dataRows.length - 1;
        }
        if (this.dataRows.length > 0) {
            const dataRow = this.dataRows[rowIndex];
            return this.selectRow(dataRow.dataKey);
        }
        return undefined;
    }

    /**
     * Inicializace atributu flag pro řádek
     * @param dataRow řádek
     * @param data data pro řádky v datagridu
     */
    public initDataRowFlag(dataRow: R, data: IRowData): void {
        if (this.flagAttributeName && data[this.flagAttributeName]) {
            const flagColor = AttrValueReader.read(data[this.flagAttributeName]) as IDataRowElement["flag"];
            const flagColorName = flagColor instanceof Enum ? flagColor.getName() : flagColor;
            if (!this.allowedFlagColors || (this.allowedFlagColors?.includes(flagColorName)))
                dataRow.flag = flagColor;
        }
    }

    /**
     * Vytvoreni jednoho radku
     */
    protected _createRow(data: IRowData): R {
        const dataRow = this._dataRowFactory(this, data);

        dataRow.init(data, this, this._createRowControlElement(dataRow));
        this.initDataRowFlag(dataRow, data);

        this.dataRows.push(dataRow);

        if (this.isSelectedAll)
            dataRow.isSelected = true;

        this._eventsController.addListener(dataRow, "rowclick", (e) => {
            this._rowClick(e as RowClickEvent, dataRow);
        });

        return dataRow;
    }

    protected _createRowControlElement(dataRow: IDataRowElement): HTMLElement | undefined {
        return this._rowControlsFactory ? this._rowControlsFactory(() => dataRow.dataKey) : undefined;
    }

    /**
     * Inicializuje radky stranky v seznamu
     * @param _page
     */
    protected async _initRows(_page: number): Promise<any> {
        this._createRows(this.dataSource);
    }

    /**
     * Vrátí vybrané dataKey řádků, pokud je výběr složitější vrací je pomocí podmínky jinak vrátí pole dataKeys
     * @param forceCondition pokud je true, vždy vrací podmínku, výchozi false
     */
    public getSelectionData(forceCondition: boolean = false): string | string[] {
        return this._selectionHelper.getSelectedData(forceCondition);
    }

    public preRender(): void {
        // Prázdná metoda
    }

    /**
     * Vykresleni datagridu
     */
    public render(): void {
        if (this.renderHeader)
            this._renderHeader();
        this._renderDataContainer();
        this._renderData();
        this._renderFooter();
    }

    protected _renderHeader(): void {
        // Prazdna metoda
    }

    protected _renderFooter(): void {
        if (!this._listDiv) {
            return;
        }
        if (!this._renderFooterEnabled) {
            if (this._footerElement) {
                this._footerElement.remove();
                this._footerElement = undefined;
            }
            if (this._fillerElement) {
                this._fillerElement.remove();
                this._fillerElement = undefined;
            }
            return;
        }

        if (this._renderFooterEnabled && !this._footerElement) {
            this._footerElement = document.createElement("div");
            this._footerElement.className = "footer-div";
            this._fillerElement = document.createElement("div");
            this._fillerElement.className = "filler-div";
            this._listDiv.append(this._fillerElement);
            this._listDiv.append(this._footerElement);
        }

        if (this._footerElement.hasChildNodes()) {
            this._footerElement.firstChild.remove();
        }

        if (this._footerContent) {
            this._footerElement.append(this._footerContent);
        }
    }

    /**
     * Vykresli pozadovanou "stranku" zaznamu
     */
    protected _renderData(eventData?: IDataSourceLoadInfo): void {
        if (!this._dataTable)
            return;

        this._updateEmptyMessageVisibility(eventData?.isFiltered, eventData?.fulltextSearchString);
        this._renderRows(this._dataTable.childElementCount + this._dataTableOffset, this.dataRows.length);

        this._updateResizeDivSize(eventData === undefined);

        this._bindingData = false;
        this.resizeGrid(eventData);
    }

    protected _renderRows(fromRowIndex: number, toRowIndex: number): void {
        for (let i = fromRowIndex; i < toRowIndex; i++) {
            this._dataTable.appendChild(this.dataRows[i].render());
        }
    }

    /**
     * Vykresleni kontejneru pro data
     */
    protected _renderDataContainer(): void {
        this._listDiv = document.createElement("div");

        this._listDiv.classList.add("list-div");
        this._resizeDiv = document.createElement("div");
        this._resizeDiv.style.minHeight = "1px";
        this._updateResizeDivSize();
        this._mainListDiv.appendChild(this._listDiv);

        const table = document.createElement("table");

        this._dataTable = document.createElement("tbody");
        this._dataTable.classList.add("table-body");

        table.appendChild(this._dataTable);

        this._listDiv.appendChild(this._resizeDiv);
        this._listDiv.appendChild(table);

        this._eventsController.addListenerWithKey("dataGridWindowReisze", window as any, "resize", () => {
            this._updateResizeDivSize();
        });

        this._eventsController.addListener(this._listDiv, "scroll", () => {
            this.classList.toggle("shadow", this._listDiv.scrollLeft > 0);
        }, { options: { passive: true } });

        this._eventsController.addListener(this._mainListDiv, "scroll", (_ev: UIEvent) => { // If resizeDiv.width = 0, the scroll listener on the listDiv doesn't work and it needs to be transferred to the mainListDiv (to display the shadow on frozenHelperDiv)
            if (this._resizeDiv.style.width === "0px" || this._resizeDiv.style.width === "") {
                this.classList.toggle("shadow", this._mainListDiv.scrollLeft > 0);
            }
        });
    }

    /**
     * Reakce na změnu třídění na sloupci.
     * @param column Sloupec, na který se kliklo
     * @param orderType Typ třídění (např. vzestupné)
     * @param addToExisting Zda se jedná o třídění se shiftem, tzn. třídění podle více sloupců
     * @param additionalOrdering Dodatečné parametry třídění (např. linguistic)
     */
    public async changeOrdering(column: Column, orderType: OrderType | null, addToExisting: boolean, additionalOrdering?: AdditionalOrdering): Promise<void> {
        if (!this.interactive || this.headerRow.disableSort)
            return;
        this.clearDataRows();

        //pokud neni stisknuty shift, tak se seznam orderby vyprazdni
        if (!addToExisting)
            this.orderBy.splice(0, this.orderBy.length);

        // pokud je defaultní třídění podle ID, tak to potřebujeme odebrat ze seznamu orderby, jinak zůstane na prvním místě a přebije ostatní sloupce, přidá se potom v jiné části kódu na konec, kam patří
        if (this.orderBy.filter(c => c.attributeName === "ID").length > 0) {
            const idColumn = this.orderBy.indexOf(this.orderBy.filter(c => c.attributeName === "ID")[0]);
            this.orderBy.splice(idColumn, 1);
        }

        for (const currentColumn of this.columns) {
            if (currentColumn === column) { // pokud to je sloupec na ktery jsem klikl
                if (orderType === OrderType.ascending || orderType === OrderType.descending) {
                    this._setColumnOrderState(column, orderType, additionalOrdering);
                    continue;
                }

                if (addToExisting) {
                    const index = DataGridSharedMethods.orderByIndexOf(this.orderBy, currentColumn.orderAttrName);

                    if (index > -1)
                        this.orderBy.splice(index, 1);
                }

                if (this.orderBy.filter(c => StringUtils.compareCaseInsensitive(c.attributeName, currentColumn.orderAttrName)).length > 0) {
                    const i = this.orderBy.indexOf(this.orderBy.filter(c => c.attributeName === currentColumn.orderAttrName)[0]);
                    this.orderBy.splice(i, 1);
                }
                column.headerCell.updateOrderType();
            } else if (!addToExisting) { // neni to sloupec na ktery jsem kliknul a nedrzim shift
                currentColumn.headerCell.updateOrderType();
            }
        }

        this._bindingData = true;
        this.dataSource.orderBy = this.orderBy;
        this._refreshSelection = false;

        this.callEvent("OrderingChanged", { order: this.orderBy } as IColumnOrderingChangedEventData);
    }

    private _setColumnOrderState(column: Column, orderType: OrderType, additionalOrdering?: AdditionalOrdering): void {
        this.callEvent("BeforeOrder");

        const index = DataGridSharedMethods.orderByIndexOf(this.orderBy, column.orderAttrName);

        if (index > -1)
            this.orderBy[index].order = orderType;
        else
            this._pushOrderBy(column.orderAttrName, orderType, additionalOrdering);

        column.headerCell.updateOrderType(orderType);
    }

    private _pushOrderBy(attrName: string, orderType: OrderType, additionalOrdering?: AdditionalOrdering): void {
        this.orderBy.push({
            attributeName: attrName,
            order: orderType,
            additionalOrdering: additionalOrdering,
        });
    }

    protected _scroll(ev: UIEvent, scrollType: string): boolean {
        // tato podminka je zde proto, ze se vyhodnocuji dve udalosti, ktere vzajemne pro navazane prvky nastavuji hodnotu scrollTop, a kdyby se v jedne udalosti nastavil scrollTop
        // pro sousedni element, doslo by ke znovuzavolání a nastaveni v sousednim elementu a tim by se rozbilo jak vyhodnocovani tak posouvani, proto pokud je vyvolana z jednoho
        // je treba jeji zpracovani v druhem zrusit
        if (this._lastScrollEvent !== scrollType && this._lastScrollEvent != null) {
            this._lastScrollEvent = null;
            return false;
        }
        this._lastScrollEvent = scrollType;
        this.scrollGrid(ev);
        return true;
    }

    /**
     * Znovu vykreslit datove radky
     */
    public async reRenderData(): Promise<void> {
        this._setLastSelectedDataKeys(this.selectedRows);

        for (const row of this.dataRows) {
            row.remove();
        }
        this.dataRows = [];

        for (const row of this.dataSource) {
            this._createComplexRow(row as IRowData);
        }

        this._renderData();
        await this.keepSelectedRows();
    }

    public scrollGrid(_ev: UIEvent): void {
        if (this._allDataRead || this._listDiv.scrollTop === 0 || this._currentSkippedRows > 0) // Pridana kontrola na to jestli se roluje s vertikalnim scrollem a pokud ne, tak neni treba nacitat data
            return;

        if (this._listDiv && this._listDiv.scrollHeight - this._listDiv.offsetHeight - this._listDiv.scrollTop < 10 && !this._bindingData) {
            this._getNextData(this.fetchCount);
        }
    }

    private _getNextData(count: number): void {
        this.currentPageIndex++;
        this._bindingData = true; //je potreba, aby se nastavilo a neslo to sem do doby, nez se vyhodnoti udalost OnLoadData, ktera vraci data
        this.dataSource.getNextData(this.startIndex, count);
    }

    public set headerColor(value: string) {
        if (this._color && this._color.length > 0)
            this.classList.remove(this._color);

        this._color = value;

        this.classList.add(value);
    }

    public get headerColor(): string {
        return this._color;
    }

    /**
     * TODO Metoda pracuje s disposed řádky. Opravit příčinu práce s disposed řádky v rámci tasku 33553.
     */
    public removeRow(dataKey: string): Promise<void> {
        const row = this.dataRows.find((item) => item.dataKey == dataKey);
        if (row) {
            if (row.isSelected) {
                row.isSelected = false;
                this.selectedRows.splice(this.selectedRows.indexOf(row), 1);
            }

            row.isDeleted = true;

            const rowIndex = this.dataRows.indexOf(row);
            this.dataRows.splice(rowIndex, 1);
            this._lastDeletedRowIndex = rowIndex;
        }

        if (this.dataRows.length === 0)
            this._updateEmptyMessageVisibility();
        //this.emptyMessage.visible = true;

        return Promise.resolve();
    }

    /**
     * Zruší označení předaného záznamu
     * @param dataKeyToDeselect Klíč záznamu, kterému se má zrušit označení
     */
    public deselectRow(dataKeyToDeselect: string): void {
        if (this._lastSelectedDataKeys.length > 0) {
            this._removeFromLastSelectedDataKeys(dataKeyToDeselect);
        } else {
            const rows = this.selectedRows;
            for (let i = 0; i < rows.length; i++) {
                if (rows[i].dataKey == dataKeyToDeselect) {
                    rows.splice(i, 1);
                    return;
                }
            }
        }
    }

    /**
     * Odstraneni vybranych radku
     */
    public removeSelectedRows(): void {
        while (this.selectedRows.length !== 0) {
            const row = this.selectedRows[0];
            row.remove();
            row.isSelected = false;
            this.dataRows.splice(this.dataRows.indexOf(row), 1);
        }
    }

    /**
     * Vyber vsech radku
     */
    public selectAllRows(): void {
        this.isSelectedAll = true;
        this._refreshSelection = false;

        for (const row of this.dataRows)
            row.isSelected = true;
        this.triggerSelectionChangedAfterTimeout("-1", false);
    }

    /**
     * Provede označení/odznačení aktuální výběru řádků
     * Není implementováno. Výběr odpovídá označeným řádkům DataGridu.
     */
    public toggleRowSelection(): void {
        // Prazdna metoda
    }

    /**
     * Stisknuti klavesy na DataGridu a jeji osetreni
     *
     * @param e
     * @param dataGrid
     */
    public static async keyDown(e: KeyboardEvent, dataGrid: DataGrid<IDataRowElement>): Promise<void> {
        if (!dataGrid.interactive)
            return;

        if (ROW_SELECTION_KEY_CODES.includes(e.code)) {
            e.preventDefault();
            dataGrid._dispatchKeyboardSelectionChangeInitiated();
            if (!(await DataGridSharedMethods.canChangeSelectedRow(dataGrid))) {
                return;
            }
        }

        switch (e.code) {
            case Code.PageUp:
                if (e.shiftKey && dataGrid.multiSelect) {
                    dataGrid.shiftPageUpPrevRecord();
                } else {
                    dataGrid.pageUpPrevRecord();
                }
                break;
            case Code.PageDown:
                if (e.shiftKey && dataGrid.multiSelect) {
                    dataGrid.shiftPageDownNextRecord();
                } else {
                    dataGrid.pageDownNextRecord();
                }
                break;
            case Code.End:
                if (e.shiftKey && dataGrid.multiSelect) {
                    dataGrid.shiftSelectEndRecord();
                } else {
                    dataGrid.endNextRecord();
                }
                break;
            case Code.Home:
                if (e.shiftKey && dataGrid.multiSelect) {
                    dataGrid.shiftSelectHomeRecord();
                } else {
                    dataGrid.homePrevRecord();
                }
                break;
            case Code.ArrowUp:
                if (e.shiftKey && dataGrid.multiSelect) {
                    dataGrid.keyPressRowIndex--;
                    dataGrid.shiftSelectRecords(e.key);
                } else {
                    dataGrid.prevRecord();
                }
                break;
            case Code.ArrowDown:
                if (e.shiftKey && dataGrid.multiSelect) {
                    dataGrid.keyPressRowIndex++;
                    dataGrid.shiftSelectRecords(e.key);
                } else {
                    dataGrid.nextRecord();
                }
                break;
            case Code.KeyA:
                if (e.ctrlKey && dataGrid.multiSelect) {
                    e.preventDefault();
                    if (!(await DataGridSharedMethods.canChangeSelectedRow(dataGrid, true))) {
                        return;
                    }
                    dataGrid.selectAllRows();
                    dataGrid.callEvent("SelectionAll");
                }
                break;
            case Code.Space:
                dataGrid.toggleRowSelection();
                break;
            default:
                break;
        }
    }

    public static keyPress(_e: KeyboardEvent, _dataGrid: DataGrid<IDataRowElement>): void {
        // Prazdna metoda
    }

    public static keyUp(e: KeyboardEvent, dataGrid: DataGrid<IDataRowElement>): void {
        if (e.key === Key.ArrowDown || e.key === Key.ArrowUp) {
            if (dataGrid._lastSelectedIndex > -1) {
                if (dataGrid.dataRows.indexOf(dataGrid.lastClickedRow) === dataGrid._lastSelectedIndex) {
                    dataGrid.triggerSelectionChangedAfterTimeout(dataGrid.lastClickedRow.dataKey);
                } else {
                    dataGrid.triggerSelectionChangedAfterTimeout("-1");
                }
            }
            dataGrid._lastSelectedIndex = -1;
        }
    }

    /**
     * Posune aktualni zaznam o jeden dale
     */
    public nextRecord(trigger = true): void {
        this._moveRecord(MoveRecordDirection.next, trigger);
    }

    /**
     * Posun na posledni nacteny zaznam
     */
    public endNextRecord(): void {
        let index: number = this.dataRows.indexOf(this.lastClickedRow);

        if (index + 1 >= this.dataRows.length)
            return; //stojim na poslednim zaznamu

        this.clearSelectedRows();
        this.dataRows.forEach(r => r.isSelected = false);

        index = this.dataRows.length - 1;

        this.dataRows[index].isSelected = true;
        this.lastClickedRow = this.selectedRows[0];

        this._listDiv.scrollTop = this._listDiv.scrollHeight;

        this.triggerSelectionChangedAfterTimeout(this.lastClickedRow.dataKey);
        //this.dispatchSelectionChangeEvent(this.LastClickedRow.DataKey, { doubleClick: false, clickedRow: this.LastClickedRow });
    }

    /**
     * Oznacovani pomoci shift + end.
     */
    public shiftSelectEndRecord(): void {
        let index: number = this.dataRows.indexOf(this.lastClickedRow);

        if (index + 1 >= this.dataRows.length)
            return; //stojim na poslednim zaznamu

        if (index < 0) {
            index = 0;
        }

        for (let i = index; i < this.dataRows.length; i++) {
            this.dataRows[i].isSelected = true;
            this._dispatchSelectionChangeEvent(this.dataRows[i].dataKey, { doubleClick: false, clickedRow: this.dataRows[i] });
        }

        index = this.dataRows.length - 1;
        this.lastClickedRow = this.dataRows[index];

        this._listDiv.scrollTop = this._listDiv.scrollHeight;
    }

    /**
     * Posun na prvni radek
     */
    public homePrevRecord(): void {
        let index: number = this.dataRows.indexOf(this.lastClickedRow);

        if (index <= 0)
            return; //stojim na prvnim

        this.clearSelectedRows();
        this.dataRows.forEach(r => r.isSelected = false);

        index = 0;

        this.dataRows[index].isSelected = true;
        this.lastClickedRow = this.selectedRows[0];

        this._listDiv.scrollTop = 0;

        this.triggerSelectionChangedAfterTimeout(this.lastClickedRow.dataKey);
        //this.dispatchSelectionChangeEvent(this.LastClickedRow.DataKey, { doubleClick: false, clickedRow: this.LastClickedRow });
    }

    /**
     * Oznacovani pomoci shift + home.
     */
    public shiftSelectHomeRecord(): void {
        const index: number = this.dataRows.indexOf(this.lastClickedRow);

        if (index <= 0)
            return; //stojim na prvnim

        for (let i = index; i >= 0; i--) {
            this.dataRows[i].isSelected = true;
            this._dispatchSelectionChangeEvent(this.dataRows[i].dataKey, { doubleClick: false, clickedRow: this.dataRows[i] });
        }

        this.lastClickedRow = this.dataRows[0];

        this._listDiv.scrollTop = 0;
    }

    /**
     * Posun o stranku dolu
     */
    public pageDownNextRecord(): void {
        const index: number = this.dataRows.indexOf(this.lastClickedRow);

        if (index + 1 >= this.dataRows.length)
            return; //stojim na poslednim zaznamu

        this.clearSelectedRows();

        let rowCntNext = Math.ceil(this._listDiv.clientHeight / (this.dataRows[0].clientHeight + 1)) + index;
        if (this.dataRows.length <= rowCntNext)
            rowCntNext = this.dataRows.length - 1;

        this.dataRows[rowCntNext].isSelected = true;
        this.lastClickedRow = this.selectedRows[0];

        this._listDiv.scrollTop = (this.selectedRows[0].clientHeight + 1) * (rowCntNext) - this._listDiv.clientHeight + (this.selectedRows[0].clientHeight + 1);

        this.triggerSelectionChangedAfterTimeout(this.lastClickedRow.dataKey);
        //this.dispatchSelectionChangeEvent(this.LastClickedRow.DataKey, { doubleClick: false, clickedRow: this.LastClickedRow });
    }

    /**
     * Oznaceni zaznamu se shiftem a posun o stranku dolu
     */
    public shiftPageDownNextRecord(): void {
        let index: number = this.dataRows.indexOf(this.lastClickedRow);

        if (index + 1 >= this.dataRows.length)
            return; //stojim na poslednim zaznamu

        let rowCntNext = Math.ceil(this._listDiv.clientHeight / (this.dataRows[0].clientHeight + 1)) + index;
        if (this.dataRows.length <= rowCntNext)
            rowCntNext = this.dataRows.length - 1;

        if (index < 0) {
            index = 0;
        }

        for (let i = index; i <= rowCntNext; i++) {
            this.dataRows[i].isSelected = true;
            this._dispatchSelectionChangeEvent(this.dataRows[i].dataKey, { doubleClick: false, clickedRow: this.dataRows[i] });
        }

        this.lastClickedRow = this.dataRows[rowCntNext];

        this._listDiv.scrollTop = (this.lastClickedRow.clientHeight + 1) * (rowCntNext) - this._listDiv.clientHeight + (this.lastClickedRow.clientHeight + 1);

        this.triggerSelectionChangedAfterTimeout(this.lastClickedRow.dataKey);
    }

    /**
     * Posun o stranku nahoru
     */
    public pageUpPrevRecord(): void {
        const index: number = this.dataRows.indexOf(this.lastClickedRow);

        if (index <= 0)
            return; //stojim na prvnim

        this.clearSelectedRows();

        let rowCntNext = index - Math.ceil(this._listDiv.clientHeight / (this.dataRows[0].clientHeight + 1));
        if (rowCntNext < 0)
            rowCntNext = 0;

        this.dataRows[rowCntNext].isSelected = true;
        this.lastClickedRow = this.selectedRows[0];

        this._listDiv.scrollTop = (this.selectedRows[0].clientHeight + 1) * (rowCntNext);

        this.triggerSelectionChangedAfterTimeout(this.lastClickedRow.dataKey);
        //this.dispatchSelectionChangeEvent(this.LastClickedRow.DataKey, { doubleClick: false, clickedRow: this.LastClickedRow });
    }

    /**
     * Oznaceni zaznamu se shiftem a posun o stranku nahoru
     */
    public shiftPageUpPrevRecord(): void {
        const index: number = this.dataRows.indexOf(this.lastClickedRow);

        if (index <= 0)
            return; //stojim na prvnim

        let rowCntNext = index - Math.ceil(this._listDiv.clientHeight / (this.dataRows[0].clientHeight + 1));
        if (rowCntNext < 0)
            rowCntNext = 0;

        for (let i = index; i >= rowCntNext; i--) {
            this.dataRows[i].isSelected = true;
            this._dispatchSelectionChangeEvent(this.dataRows[i].dataKey, { doubleClick: false, clickedRow: this.dataRows[i] });
        }

        this.lastClickedRow = this.dataRows[rowCntNext];

        this._listDiv.scrollTop = (this.lastClickedRow.clientHeight + 1) * (rowCntNext);

        this.triggerSelectionChangedAfterTimeout(this.lastClickedRow.dataKey);
    }

    /**
     * Posune aktualni zaznam na predchozi
     */
    public prevRecord(trigger = true): void {
        this._moveRecord(MoveRecordDirection.prev, trigger);
    }

    /**
     * Vyber zaznamu od posledniho kliknuteho po posledni index (pri vyberu zaznamu Shift + Up/Down)
     * @param key - Kod klavesy - sipka nahoru nebo dolu
     */
    public shiftSelectRecords(_key: string = undefined): void {
        this.isSelectedAll = false;
        // mam uz oznaceny radek (radky)
        if (this.selectedRows.length > 0) {
            const lastClickedRowIndex = this.dataRows.indexOf(this.selectionStartRow);
            let fromIndex = lastClickedRowIndex; // start index
            let toIndex = this.keyPressRowIndex; // index aktualniho
            let direction = MoveRecordDirection.next;

            if (this._lastSelectedIndex === -1) {
                this.clearSelectedRows();
            } else {
                // pokud uz mam oznaceno vice zaznamu, neni potreba oznacovat vsechny znovu a odpichnu se od naposledy oznaceneho
                fromIndex = this._lastSelectedIndex;
            }

            if (fromIndex > toIndex) { //v pripade ze index prvniho je vetsi, tak prohodime
                direction = MoveRecordDirection.prev;
                const tmp = fromIndex;
                fromIndex = toIndex;
                toIndex = tmp;
            }

            if (fromIndex < 0)
                this.keyPressRowIndex = fromIndex = 0;

            if (toIndex >= this.dataRows.length)
                this.keyPressRowIndex = toIndex = this.dataRows.length - 1;

            const select = this._lastSelectedIndex === -1 ? true
                // pokud jiz mam oznaceno vice zaznamu, je treba zjistit zda chci dalsi oznacit nebo odznacit (= zda rozsiruji nebo zuzuji vyber)
                : Math.abs(lastClickedRowIndex - this.keyPressRowIndex) >= Math.abs(lastClickedRowIndex - this._lastSelectedIndex);
            for (let i = fromIndex; i <= toIndex; i++) { // prochazime a oznacime/odznacime radky od fromIndexu po toIndex
                this.dataRows[i].isSelected = i === lastClickedRowIndex ? true : select;
            }

            this._lastSelectedIndex = this.keyPressRowIndex;
            this.lastClickedRow = this.dataRows[direction === MoveRecordDirection.next ? toIndex : fromIndex];
            this._adjustScrollTop(direction, this.lastClickedRow);
        }
    }

    protected _moveRecord(direction: MoveRecordDirection, trigger = true): void {
        try {
            let index: number = -1;
            if (this.lastClickedRow != null) {
                index = this.keyPressRowIndex;
            }
            if (direction == MoveRecordDirection.next && index + 1 >= this.dataRows.length)
                return; //stojim na poslednim zaznamu
            else if (direction == MoveRecordDirection.prev && index <= 0)
                return; //stojim na prvnim

            this.clearSelectedRows();
            this.dataRows[index + direction].isSelected = true;
            this.lastClickedRow = this.selectedRows[0];
            this.selectionStartRow = this.selectedRows[0];

            this._adjustScrollTop(direction);

            if (trigger)
                this.triggerSelectionChangedAfterTimeout(this.lastClickedRow.dataKey);
            //this.dispatchSelectionChangeEvent(this.LastClickedRow.DataKey, { doubleClick: false, clickedRow: this.LastClickedRow });
        } finally {
            // Prázdné finally
        }
    }

    protected _adjustScrollTop(direction: MoveRecordDirection, element: HTMLElement = this.lastClickedRow): void {
        if (!element)
            return;

        //pokud byl záznam odscrolovaný a nově vybraný záznam není ve viewportu, tak na něj odscrolluje
        //clientHeight/2 v druhé části je  záměrně. Chceme při posunu posledního zobrazeného řádku, aby se řádek posunul o 1 dolů. Kdyby tam bylo +clientHeight, tak by to poskočilo o stránku a nový řádek by byl první nahoře.
        const isElementInViewport = element.offsetTop > this._listDiv.scrollTop && element.offsetTop - (element.clientHeight / 2) < (this._listDiv.scrollTop + this._listDiv.clientHeight);
        if (!isElementInViewport) {
            this._listDiv.scrollTop = element.offsetTop;
            return;
        }

        if (direction == MoveRecordDirection.next) {
            const VISIBLE_RANGE = this._listDiv.scrollTop + this._listDiv.clientHeight - (element.clientHeight - 10);
            if (VISIBLE_RANGE < element.offsetTop) {
                this._listDiv.scrollTop = this._listDiv.scrollTop + element.clientHeight + 1;
            }
        } else { //move prev
            const VISIBLE_RANGE = this._listDiv.scrollTop - element.clientHeight / 2;

            if (VISIBLE_RANGE >= element.offsetTop) {
                this._listDiv.scrollTop = this._listDiv.scrollTop - element.clientHeight - 1;
            }
        }
    }

    /**
   * Potvrzeni vybrani zaznamu - zatim vyuzite jen v LOV pri Enter
   */
    public confirmSelection(): void {
        this._dispatchSelectionChangeEvent(this.lastClickedRow.dataKey, { doubleClick: false, clickedRow: this.lastClickedRow });
    }

    /**
     * Spusteni SelectionChanged po 300ms od posledniho volani. Pouziva se pri oznacovani novych .
     * Vlozeni timeoutu, z duvodu zbytecne prodlevy a zateze serveru mezi rychlím preklikavanim nad jednotlivymi zaznamy
     */
    public triggerSelectionChangedAfterTimeout(rowID: string, timeout: boolean = true): void {
        if (this.getSelectedRowInfoTimer)
            clearTimeout(this.getSelectedRowInfoTimer);
        this.getSelectedRowInfoTimer = self.setTimeout(() => {
            this._dispatchSelectionChangeEvent(rowID, { doubleClick: false, clickedRow: this.lastClickedRow });
        }, timeout ? 300 : 0);
    }

    /**
     * Vybere první řádek v seznamu, pokud už není vybraný
     * @param autoSelectOnlyIfOneRow - pouze pokud je nacten jen jeden radek
     */
    public selectFirstRow(autoSelectOnlyIfOneRow: boolean = false): void {
        if (this.dataRows.length === 0) {
            return;
        }

        if (((autoSelectOnlyIfOneRow && this.dataRows.length === 1) || !autoSelectOnlyIfOneRow) && (!this.dataRows[0].isSelected || this.lastClickedRow !== this.dataRows[0])) {
            this.dataRows[0].isSelected = true;
            this.lastClickedRow = this.dataRows[0];
            this.selectionStartRow = this.dataRows[0];
            this._dispatchSelectionChangeEvent(this.lastClickedRow.dataKey, { doubleClick: false, clickedRow: this.lastClickedRow });
        }
    }

    /**
     * Vloží nový element reprezentující existující záznam a vrátí ho
     * @param _dataRow Data nového záznamu
     */
    public insertExistingRecord(_dataRow: IRowData): R {
        throw new Error("Not implemented");
    }

    /**
     * Vytvoří a vloží nový element reprezentující nový záznam
     * @param _dataRow Data nového záznamu
     */
    public insertNewRecord(_dataRow: IRowData): R {
        throw new Error("Not implemented");
    }

    public setFilters(filters: { attrName: string; filterElement: HTMLSpanElement }[]): void {
        for (const column of this.columns) {
            column.headerTemplate.setFilter();
        }
        if (filters && filters.length > 0) {
            for (const filter of filters) {
                const column = this.columns.find((result) => StringUtils.compareCaseInsensitive(result.filterAttrName, filter.attrName));
                if (column)
                    column.headerTemplate.setFilter(filter.filterElement);
            }
        }

        this._selectionHelper.filterCondition = this.dataSource.getFilterCondition();
    }

    /**
     * Funkce aktualizuje šířku resize divu na základě toho zda je nebo memé zobrazen vertikální scrollbar
     * @param dataLoadedRequired Pžíznak zda je pro nastavení šířky vyžadováno nastavení _dataLoaded. Využívá se pokud je jasné, že se data právě načtou, ale proměnná ještě není nastavená na true
     * @returns
     */
    protected _updateResizeDivSize(dataLoadedRequired = true): void {
        if (!this.headerRow || !this._resizeDiv || !this._dataLoaded && dataLoadedRequired) // Před načtením dat nechceme nastavovat šířku resizeDivu - zobrazily by se dva scrollbary (listDivu a mainListDivu)
            return;

        const isVerticalScrollbarVisible = this._dataTable && this._dataTable.clientHeight > this._listDiv.clientHeight;
        const scrollBarWidth = isVerticalScrollbarVisible ? getScrollBarWidth() : 0; // Šířku scrollbaru je potřeba odečíst v případě, že není zobrazen

        const columnsWidth = this.headerRow.getAllColumnsWidth();
        this._resizeDiv.style.width = (columnsWidth - scrollBarWidth) + "px";
    }

    public resumeLoading(): void {
        if (this._dataLoadingPaused)
            this.resizeGrid();
    }

    public getExtendedProperties(attrName: string): ExtendedPropertiesType {
        return this._extendedProperties?.get(attrName);
    }

    public setExtendedProperties(extendedProperties: CaseInsensitiveMap<ExtendedPropertiesType>): void {
        this._extendedProperties = extendedProperties;
    }

    /**
     * Zkontroluje, zda je DataGrid disposed a pokud ano zaloguje warning o volání metody nad disposed DataGridem.
     *
     * Tato metoda a obecně kontroly na dispose by neměly být nutné po odstranění příčin, kvůli kterým se pracuje i nadále s disposed objekty.
     * Pro řešení příčin je vytvořený task 33553.
     *
     * @param methodName Název metody, kde se provádí kontrola. Slouží pro logování.
     * @return true, pokud je DataGrid disposed, jinak false.
     */
    protected checkAndLogDisposedUsage(methodName: string): boolean {
        const disposed = this.isDisposed();
        if (disposed) {
            logger.warn(`Calling ${methodName} on disposed ${this.constructor.name}!`);
        }
        return disposed;
    }

    public isDisposed(): boolean {
        return this._isDataGridDisposed;
    }

    public override dispose(): void {
        this.clearDataRows();
        this.dataRows = [];
        this._visibilityObserver.disconnect();
        this._resizeObserver.disconnect();

        this._columns?.forEach((column) => column.dispose());
        this._columns = [];

        this._afterFetchNoReselectCallback = null;
        this.selectedRows = [];

        this.headerRow?.dispose();
        if (this.emptyMessage && this.emptyMessage instanceof IDisposable) {
            this.emptyMessage.dispose();
        }

        super.dispose();
        this._isDataGridDisposed = true;
    }

    //#endregion Methods
}

export enum MoveRecordDirection {
    prev = -1,
    next = 1,
}
